// WHAT: Main app component for settings management with tab navigation
// WHY:  Provides centralized settings interface for all application configurations
// DOMAIN: Settings
// DEPENDENCIES: React Router, UI components, Lucide icons
// RISKS:
//   - NOTE: Tab configuration is hardcoded - may need updates when adding new settings
//   - NOTE: Tab matching logic may not work correctly with nested routes
//   - NOTE: Description text is in English - may need internationalization

import {
  User,
  Database,
  Code,
  MessageSquare,
  Heart,
  Scale,
  Apple,
  Zap,
  Clock,
  Bot,
  Home,
  FileText,
} from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { Card } from "@/components/ui";

/**
 * WHAT:
 *   Main app component that provides centralized settings interface with tab
 *   navigation for all application configuration options.
 *
 * WHY:
 *   Enables users to manage all application settings from one unified interface
 *   with clear navigation and organization.
 *
 * BEHAVIOR:
 *   - Displays tab navigation with icons
 *   - Shows current tab description
 *   - Renders tab content via Outlet
 *   - Handles tab switching via navigation
 *   - Uses TracQ branding styles
 *
 * RISKS:
 *   - NOTE: Tab configuration is hardcoded - may need updates when adding new settings
 *   - NOTE: Tab matching logic may not work correctly with nested routes
 *   - NOTE: Description text is in English - may need internationalization
 *
 * @returns {JSX.Element} Settings app component with tab navigation
 *
 */
export function SettingsApp() {
  const location = useLocation();
  const navigate = useNavigate();

  // Tab configuration with icons
  const tabs = [
    {
      description: "Manage your personal information and preferences",
      icon: User,
      id: "personal",
      label: "Personal Information",
      path: "/settings/personal",
    },
    {
      description: "Configure your Supabase database connection",
      icon: Database,
      id: "supabase",
      label: "Supabase",
      path: "/settings/supabase",
    },
    {
      description: "GitHub integration settings",
      icon: Code,
      id: "github",
      label: "GitHub",
      path: "/settings/github",
    },
    {
      description: "OpenAI ChatGPT integration settings",
      icon: MessageSquare,
      id: "chatgpt",
      label: "ChatGPT",
      path: "/settings/chatgpt",
    },
    {
      description: "Oura health ring integration settings",
      icon: Heart,
      id: "oura",
      label: "Oura",
      path: "/settings/oura",
    },
    {
      description: "PDF parsing and extraction configuration",
      icon: FileText,
      id: "parqet",
      label: "Parqet",
      path: "/settings/parqet",
    },
    {
      description: "Withings health scale integration settings",
      icon: Scale,
      id: "withings",
      label: "Withings",
      path: "/settings/withings",
    },
    {
      description: "Yazio nutrition app integration settings",
      icon: Apple,
      id: "yazio",
      label: "Yazio",
      path: "/settings/yazio",
    },
    {
      description: "MiracleFlow productivity integration settings",
      icon: Zap,
      id: "miracle-flow",
      label: "MiracleFlow",
      path: "/settings/miracle-flow",
    },
    {
      description: "TimeTracQ time tracking settings",
      icon: Clock,
      id: "timetracq",
      label: "TimeTracQ",
      path: "/settings/timetracq",
    },
    {
      description: "SwitchBot smart home integration settings",
      icon: Bot,
      id: "switchbot",
      label: "SwitchBot",
      path: "/settings/switchbot",
    },
    {
      description: "Tuya smart home integration settings",
      icon: Home,
      id: "tuya",
      label: "Tuya",
      path: "/settings/tuya",
    },
  ];

  const currentTab =
    tabs.find(
      (tab) => location.pathname === tab.path || location.pathname.startsWith(tab.path + "/"),
    ) ?? tabs[0];

  const handleTabClick = (path: string) => {
    // Reason: Navigation is fire-and-forget, errors are handled by router
    // navigate from react-router-dom may return void or Promise, but errors are handled by router
    const navResult = navigate(path);
    if (navResult && typeof navResult === "object" && "catch" in navResult) {
      // Reason: navigate may return Promise with catch method, but errors are handled by router

      // Reason: TypeScript infers navigate return type incorrectly, but router handles errors
      (navResult as { catch: (handler: () => void) => void }).catch(() => {
        // NOTE: Navigation failed - silently ignore as router handles errors
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* TracQ Header */}
        <Card
          className="mb-8 border-tracq-deep-blue/30 bg-gradient-to-r from-slate-800/50 to-slate-700/30"
          padding="lg"
          variant="default"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
                <p className="text-slate-300">{currentTab?.description ?? ""}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Tab Navigation */}
        <nav className="border-b border-slate-700 mb-6">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => {
              const isActive = currentTab?.id === tab.id;
              const IconComponent = tab.icon;

              return (
                <button
                  className={`py-3 px-4 border-b-2 font-medium text-sm transition-all duration-200 flex items-center space-x-2 whitespace-nowrap ${
                    isActive
                      ? "border-tracq-bright-blue text-tracq-bright-blue"
                      : "border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-600"
                  }`}
                  key={tab.id}
                  onClick={() => {
                    handleTabClick(tab.path);
                  }}
                  title={tab.description}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Tab Content */}
        <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
          <Outlet />
        </Card>
      </div>
    </div>
  );
}
